import { useState } from 'react';
import type { SortDirection } from '@/shared/api';
import { columnWidth, fixedTableStyle, textWidth, type ColumnWidth } from '@/shared/lib';
import { ColGroup } from '@/shared/ui';
import { readValue, rowCurrency } from '../model/records';
import type { AnalyticsColumn, AnalyticsObject, ColumnKind } from '../model/schema';
import { CellValue } from './CellValue';

interface AnalyticsTableProps {
  object: AnalyticsObject;
  rows: readonly object[];
  /** Index of the first row on this page, used for row keys (rows have no guaranteed key). */
  rowOffset: number;
  caption?: string;
  /** Columns to show (default: all columns of the object). */
  columns?: readonly AnalyticsColumn[];
  sort?: { sortBy?: string; sortDirection: SortDirection };
  /** When provided, column headers become sort toggles. */
  onToggleSort?: (column: string) => void;
  /** When provided, a double-click (or Enter on a focused row) opens the row's form. */
  onOpenRow?: (row: object) => void;
}

/** Clicks on links and buttons inside a row act on those, not on the row. */
const isControl = (target: EventTarget) =>
  target instanceof Element && target.closest('a, button') !== null;

const isNumeric = (column: AnalyticsColumn) =>
  column.kind === 'integer' || column.kind === 'decimal' || column.kind === 'float';

/**
 * Widest value of a column kind in px (13px figures): "2026-10-14 12:34:56 +02:00", a shortened
 * GUID ("f2f99afc…"), "Yes"… Text is sized from its declared length below.
 */
const CONTENT: Partial<Record<ColumnKind, number>> = {
  integer: 72,
  decimal: 88,
  float: 88,
  bool: 32,
  uuid: 72,
  date: 80,
  time: 64,
  datetime: 140,
  datetimeoffset: 190,
};
/** Text columns show at most this many characters (`.cell-text` max-width). */
const TEXT_CHARS = 40;

/**
 * Fixed width per column: its widest value or its header label, whichever is wider. Text columns
 * fit their declared length up to 40 characters, so the rows on the page never move the columns.
 */
function columnWidths(columns: readonly AnalyticsColumn[]): ColumnWidth[] {
  return columns.map((column, index) => {
    const chars =
      column.maxLength && column.maxLength > 0
        ? Math.min(column.maxLength, TEXT_CHARS)
        : TEXT_CHARS;
    const content = CONTENT[column.kind] ?? textWidth(chars);
    return columnWidth(column.label, content, index === 0 || index === columns.length - 1);
  });
}

/** Generic, schema-driven `table.table` for any reporting table or view. */
export function AnalyticsTable({
  object,
  rows,
  rowOffset,
  caption = object.title,
  columns = object.columns,
  sort,
  onToggleSort,
  onOpenRow,
}: AnalyticsTableProps) {
  // A click selects a row; the selection belongs to this page of rows.
  const [selected, setSelected] = useState<{ rows: readonly object[]; index: number } | null>(null);
  const widths = columnWidths(columns);
  return (
    <div className="table-wrap">
      <table className="table table--fixed" style={fixedTableStyle(widths)} aria-label={caption}>
        <ColGroup widths={widths} />
        <thead>
          <tr>
            {columns.map((column) => {
              const direction =
                sort?.sortBy?.toLowerCase() === column.name.toLowerCase()
                  ? sort.sortDirection
                  : undefined;
              const ariaSort =
                direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
              return (
                <th
                  key={column.name}
                  scope="col"
                  className={isNumeric(column) ? 'r' : undefined}
                  aria-sort={onToggleSort ? ariaSort : undefined}
                  title={column.sqlType}
                >
                  {onToggleSort ? (
                    <button
                      type="button"
                      className="th-sort"
                      onClick={() => {
                        onToggleSort(column.name);
                      }}
                    >
                      {column.label}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={rowOffset + index}
              {...(onOpenRow && {
                className:
                  selected?.rows === rows && selected.index === index ? 'is-selected' : undefined,
                tabIndex: 0,
                'data-open': '',
                onClick: () => {
                  setSelected({ rows, index });
                },
                onDoubleClick: (event) => {
                  if (!isControl(event.target)) onOpenRow(row);
                },
                onKeyDown: (event) => {
                  if (event.key === 'Enter' && event.target === event.currentTarget) onOpenRow(row);
                },
              })}
            >
              {columns.map((column) => (
                <td key={column.name} className={isNumeric(column) ? 'r' : undefined}>
                  <CellValue
                    column={column}
                    value={readValue(row, column)}
                    currency={rowCurrency(object, row)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="empty" hidden={rows.length > 0}>
        No matching records.
      </p>
    </div>
  );
}
